import React, { useCallback, useMemo, useState } from 'react';
import ItemContent from '../../../components/ItemContent/ItemContent';
import { IEditForm } from '../../../types/interfaces/IEditForm';
import { EStatus } from '../../../types/enums/EStatus';
import { useTranslation } from 'react-i18next';
import tPaths from '../../../utils/tPaths';
import { endpoints } from '../../../utils/endpoints';
import { EPaths } from '../../../types/enums/EPaths';
import {
  CAMPAIGN_CODE_TYPE_OPTIONS,
  CAMPAIGN_TYPE_OPTIONS,
  ECampaignCodeType,
  NSPromoCampaigns,
} from '../../../types/interfaces/NSPromoCampaigns';
import useApi from '../../../utils/useApi';

interface IData {
  carrierIdentifier: string;
  name: string;
}

const AddPromoCampaigns = () => {
  const { t } = useTranslation();
  const [codeType, setCodeType] = useState<ECampaignCodeType | null>(ECampaignCodeType.Generic);
  const { data } = useApi<IData[]>(endpoints.promoCampaignsPackages);

  const dataMapper = useCallback((): IEditForm[][] => {
    return [
      [
        {
          type: 'input',
          fieldName: 'internalName',
          label: t(tPaths.editFormLabels.internalName),
          initialValue: '',
        },
        {
          type: 'select',
          fieldName: 'codeType',
          label: t(tPaths.editFormLabels.genericUniqueCode),
          variants: CAMPAIGN_CODE_TYPE_OPTIONS,
          value: codeType,
          initialValue: ECampaignCodeType.Generic,
          onChange: (_form, value) => setCodeType(value),
        },
        {
          type: 'input',
          fieldName: 'publicCode',
          label: t(tPaths.editFormLabels.publicCode),
          hidden: codeType ? codeType !== ECampaignCodeType.Generic : false,
          initialValue: '',
          uppercased: true,
        },
        {
          type: 'codesGeneration',
          fieldName: 'promoCodePrefix',
          hidden: codeType ? codeType !== ECampaignCodeType.Unique : false,
          initialValue: '',
          disabled: true,
        },
        {
          type: 'select',
          fieldName: 'package',
          label: t(tPaths.editFormLabels.package),
          variants: data ? data?.map((item) => ({ value: item.carrierIdentifier, label: item.name })) : [],
        },
        {
          type: 'select',
          fieldName: 'type',
          label: t(tPaths.editFormLabels.campaignType),
          variants: CAMPAIGN_TYPE_OPTIONS,
        },
        {
          type: 'input',
          fieldName: 'technicalCode',
          label: t(tPaths.editFormLabels.technicalCode),
          initialValue: '',
        },
        {
          type: 'input',
          fieldName: 'affiliateCode',
          label: t(tPaths.editFormLabels.affiliateCode),
          initialValue: '',
        },
        {
          type: 'switch',
          fieldName: 'oncePerDevice',
          label: t(tPaths.editFormLabels.oncePerDevice),
          initialValue: true,
        },
        {
          type: 'switch',
          fieldName: 'oncePerUser',
          label: t(tPaths.editFormLabels.oncePerUser),
          initialValue: true,
        },
      ],
    ];
  }, [t, codeType, data]);

  const updateDataFormatter = useCallback(
    (fields: any) => ({
      internalName: fields.internalName.value,
      codeType: fields.codeType.value,
      publicCode: fields.publicCode.value ? fields.publicCode.value : undefined,
      promoCodePrefix: fields.promoCodePrefix.value ? fields.promoCodePrefix.value : undefined,
      technicalCode: fields.technicalCode.value,
      affiliateCode: fields.affiliateCode.value,
      type: fields.type.value,
      targetPackageId: fields.package.value,
      oncePerUser: fields.oncePerUser.value,
      oncePerDevice: fields.oncePerDevice.value,
      status: EStatus.Inactive,
    }),
    [],
  );

  return (
    <ItemContent
      title={t(tPaths.itemContent.addPromoCampaign)}
      path={EPaths.PromoCampaigns}
      contentMapper={dataMapper}
      updateDataFormatter={updateDataFormatter}
      addItem={true}
      apiUrl={endpoints.promoCampaigns}
    />
  );
};

export default AddPromoCampaigns;
